import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { ApiError, AuthService, ConfigService, MessageService } from '../../../../core';
import { BaseTypeahead, BaseTypeaheadService } from '../../../../core/services/typeahead';
import { ConfigEmpresa } from '../../../configuracoes/app/empresa';
import { LeadFilter } from '../filter';
import { LeadSearch } from '../lead-search';
import { LeadSearchOrderBy } from '../lead-search-orderby';
import { LeadSearchHttpService } from '../services';

@Component({
    selector: 'sgt-lead-typeahead',
    templateUrl: './lead-typeahead.component.html',
    styleUrls: ['./lead-typeahead.component.less'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => LeadTypeaheadComponent), multi: true },
    ]
})
export class LeadTypeaheadComponent implements OnInit, AfterViewInit, BaseTypeahead<LeadSearch> {
    configEmpresa: ConfigEmpresa;
    isRepresentante: boolean;

    @Input() entity: LeadSearch;
    @Output() ngModelChange = new EventEmitter<LeadSearch>();

    @Input() required: any;
    @Input() disabled: any;
    @Input() readonly: any;
    @Input() ngControl: NgControl;
    @Input() autofocus = false;
    @Input() container = '';
    @Input() dropup = false;
    @Input() typeaheadOptionsInScrollableView = 5; // Quantidade de itens visíveis no no scroll do typeahead
    @Output() typeaheadChange = new EventEmitter<LeadSearch>();
    @Output() entitySelected = new EventEmitter<LeadSearch>(); // Emite evento quando a entidade foi selecionada no typeahead
    @Output() clearEntity = new EventEmitter<any>();

    /** Se deve exibir a opção de criar um novo lead quando nenhum resultado for encontrado */
    @Input() createWhenNoResults = false;
    /** Emite evento para criar um novo lead passando como valor o nome digitado no typeahead */
    @Output() create = new EventEmitter<string>();

    @ViewChild('inputTypeahead', {static: true}) inputTypeahead: ElementRef;
    typeaheadOptionsLimit = 1000; // Quantidade máxima de registros exibidos no typeahead
    typeaheadSelected: string;
    typeaheadToken: string;
    typeaheadDataSource: Observable<LeadSearch[]>;
    typeaheadNoResults: boolean;

    currentRequest: Subscription;
    isLoading: boolean;

    propagateChange: any = () => {};

    constructor(private authService: AuthService,
                private configService: ConfigService,
                private baseTypeaheadService: BaseTypeaheadService,
                private messageService: MessageService,
                private leadSearchHttpService: LeadSearchHttpService) { }

    ngOnInit() {
        this.configEmpresa = this.configService.getEmpresa();
        this.isRepresentante = this.authService.isRepresentante();

        this.createDataSource();
    }

    ngAfterViewInit() {
        if (this.autofocus) {
            jQuery(this.inputTypeahead.nativeElement).trigger('focus');
        }
    }

    /** Cria o filtro para pesquisar os leads */
    private createFilter(): LeadFilter {
        const filter = new LeadFilter();
        // Filter psq
        filter.filterPsq = this.typeaheadToken;

        return filter;
    }

    /** Cria o data source do typeahead */
    createDataSource() {
        this.typeaheadDataSource = new Observable((observer: any) => {
            if (this.currentRequest) {
                this.currentRequest.unsubscribe();
            }

            const filter = this.createFilter();
            this.currentRequest = this.leadSearchHttpService.search(filter, LeadSearchOrderBy.Nome, 'asc', this.typeaheadOptionsLimit, 0)
                                        .subscribe(
                                            (entities: LeadSearch[]) => observer.next(entities),
                                            (error: ApiError) => {
                                                this.messageService.error(error.message, 'Ocorreu um erro ao pesquisar os leads. Tente novamente', error);
                                                observer.next([]);
                                            }
                                        );
        });
    }


    /** Ao digitar no input do typeahead */
    onKeyup(e: KeyboardEvent) {
        this.baseTypeaheadService.onKeyup(this, e);
    }

    /** Ao sair do input do typeahead */
    onBlur() {
        if (this.typeaheadNoResults && this.createWhenNoResults) {
            return;
        }

        this.baseTypeaheadService.onBlur(this);
        if (!this.entity) {
            this.clearEntity.emit();
        }
    }

    /** Verifica se o typeahead possui resultados */
    onChangeTypeaheadNoResults(noResults: boolean) {
        this.typeaheadNoResults = noResults;
    }

    /** Carrega o lead informado para o typeahead */
    private load(idLead: number): Promise<LeadSearch> {
        return new Promise<LeadSearch>((resolve, reject) => {
            this.leadSearchHttpService.searchOne(idLead)
                .pipe(
                    take(1)
                )
                .subscribe(
                    lead => resolve(lead),
                    (error: ApiError) => {
                        this.messageService.error(error.message, 'Erro ao carregar o lead', error);
                        reject();
                    }
                );
        });
    }


    /** Seta o lead informado no typeahead */
    setEntity(idLead: number) {
        this.load(idLead)
            .then(lead => {
                this.selectEntity(lead);
            })
            .catch(() => {});
    }

    /** Seleciona o lead no typeahead */
    private selectEntity(lead: LeadSearch) {
        this.entity = lead ?? null;

        this.propagateChange(this.entity);
        this.typeaheadChange.emit(this.entity);
        this.entitySelected.emit(this.entity);
        this.writeValue(this.entity);
        jQuery(this.inputTypeahead.nativeElement).trigger('focus');
    }

    /** Ao selecionar o lead no typeahead */
    onSelectEntity(tm: TypeaheadMatch) {
        const lead = tm.item as LeadSearch;
        this.baseTypeaheadService.onSelectEntity(this, tm);
        this.entitySelected.emit(this.entity);
    }

    /** Ao selecionar o lead no modal de pesquisa */
    onSelectEntityModal(lead: LeadSearch) {
        this.selectEntity(lead);
    }

    /** Limpa o typeahead e o model */
    clear() {
        this.baseTypeaheadService.clear(this);
        this.clearEntity.emit();
    }

    /** Seta o foco no input */
    setFocus() {
        jQuery(this.inputTypeahead.nativeElement).trigger('focus');
    }


   /** Quando recebe um objeto lead de "fora" do component */
    writeValue(value: LeadSearch) {
        if (value) {
            this.baseTypeaheadService.writeValue(this, value, value.nome);
        } else {
            this.baseTypeaheadService.writeValue(this, null);
        }
    }

    /** Propaga as alterações */
    registerOnChange(fn) {
        this.propagateChange = fn;
    }

    registerOnTouched() {}
}
